/*
 * The dialog's half of the Brand Templates conversation. The org fetch is lazy (request() on tab mount).
 * `switchTo` and `insert` only message the pane, which does the work; `insert` settles only on a failure.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { readBrandTemplatesHandoff } from "../services/brandTemplatesHandoff";
import { subscribeBrandChannel } from "../services/dialogBrandChannelBridge";
import { postToPane } from "../services/dialogToPaneMessaging";
import {
  buildBrandThumbnailLookup,
  logSavedBrandTemplatesError,
  type SavedBrandTemplate,
} from "../../apply-brand/services/brandTemplatesApi";
import { sasVersionHeaders } from "../../shared/services/blobImageUpload";
import { getErrorMessage } from "../../shared/services/connectorErrors";

const NO_CHANNEL_MESSAGE =
  "This window can't reach the task pane, so brand templates can't be loaded. Close it and try again.";
const STAGED_LIST_MISSING_MESSAGE = "The brand template list arrived empty. Try again.";
const NO_INSERT_CHANNEL_MESSAGE =
  "This window can't reach the task pane, so the layouts can't be inserted. Close it and try again.";
const WINDOW_CLOSED_MESSAGE = "The Asset Library window closed before the insert finished.";
const SUPERSEDED_MESSAGE = "Superseded by a newer insert.";

interface PendingInsert {
  readonly resolve: (inserted: number) => void;
  readonly reject: (error: Error) => void;
}

export type DialogBrandTemplatesState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly message: string }
  | { readonly status: "ready"; readonly org: readonly SavedBrandTemplate[] };

export interface DialogBrandTemplates {
  readonly state: DialogBrandTemplatesState;
  /** Asks the pane for the list. Safe to call more than once — only the
   *  first call (per idle state) actually sends a request. */
  readonly request: () => void;
  readonly retry: () => void;
  readonly switchTo: (id: string) => void;
  /** Tells the pane the previewed template this id names has vanished from
   *  the library (EC-08), so it can sync its own selection before this
   *  window closes. */
  readonly notifyUnavailable: (id: string) => void;
  /** Asks the pane to insert these 1-based layouts of one template; resolves with the count, rejects with the pane's message. */
  readonly insert: (templateId: string, slideIndexes: readonly number[]) => Promise<number>;
}

export function useDialogBrandTemplates(): DialogBrandTemplates {
  const [state, setState] = useState<DialogBrandTemplatesState>({ status: "idle" });
  const isMountedRef = useRef(true);
  // One insert in flight at a time (the Insert button is disabled meanwhile),
  // so a single slot is enough.
  const pendingInsertRef = useRef<PendingInsert | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // The window is going away with a request unanswered; settle it so
      // nothing is left hanging on it.
      pendingInsertRef.current?.reject(new Error(WINDOW_CLOSED_MESSAGE));
      pendingInsertRef.current = null;
    };
  }, []);

  useEffect(() => {
    return subscribeBrandChannel((message) => {
      if (!isMountedRef.current) return;
      switch (message.type) {
        case "brand-templates-ready": {
          const org = readBrandTemplatesHandoff();
          setState(org ? { status: "ready", org } : { status: "error", message: STAGED_LIST_MISSING_MESSAGE });
          return;
        }
        // Insert replies settle the pending insert instead of touching the
        // list state — the grid itself hasn't changed.
        case "brand-insert-done": {
          const pending = pendingInsertRef.current;
          pendingInsertRef.current = null;
          pending?.resolve(message.inserted);
          return;
        }
        case "brand-insert-error": {
          const pending = pendingInsertRef.current;
          pendingInsertRef.current = null;
          pending?.reject(new Error(message.message));
          return;
        }
        case "brand-templates-error":
          setState({ status: "error", message: message.message });
      }
    });
  }, []);

  const request = useCallback(() => {
    setState((prev) => (prev.status === "idle" ? { status: "loading" } : prev));
    if (!postToPane({ type: "brand-templates-request" }, "asset-library-dialog-brand-templates-requested")) {
      setState({ status: "error", message: NO_CHANNEL_MESSAGE });
    }
  }, []);

  const retry = useCallback(() => {
    setState({ status: "loading" });
    if (!postToPane({ type: "brand-templates-refresh" }, "asset-library-dialog-brand-templates-refresh-requested")) {
      setState({ status: "error", message: NO_CHANNEL_MESSAGE });
    }
  }, []);

  const switchTo = useCallback((id: string) => {
    postToPane({ type: "switch-brand-template", id }, "asset-library-dialog-switch-brand-template");
  }, []);

  const notifyUnavailable = useCallback((id: string) => {
    postToPane({ type: "brand-template-unavailable", id }, "asset-library-dialog-brand-template-unavailable");
  }, []);

  const insert = useCallback(
    (templateId: string, slideIndexes: readonly number[]) =>
      new Promise<number>((resolve, reject) => {
        if (!postToPane({ type: "brand-templates-insert", templateId, slideIndexes }, "asset-library-dialog-brand-insert-requested")) {
          reject(new Error(NO_INSERT_CHANNEL_MESSAGE));
          return;
        }
        // Replace (rather than queue) any earlier pending insert: the footer
        // only allows one at a time, so an existing entry here is stale.
        pendingInsertRef.current?.reject(new Error(SUPERSEDED_MESSAGE));
        pendingInsertRef.current = { resolve, reject };
      }),
    [],
  );

  return { state, request, retry, switchTo, notifyUnavailable, insert };
}

/* ── Slide thumbnails ────────────────────────────────────────────── */

/*
 * Rendered slide thumbnails. The `.cache` file is downloaded once per URL; a failed or malformed one
 * yields an empty lookup, so cards keep their placeholder.
 */

type ThumbnailLookup = ReadonlyMap<string, string>;

const EMPTY_LOOKUP: ThumbnailLookup = new Map();
const lookupCache = new Map<string, Promise<ThumbnailLookup>>();

async function downloadLookup(cacheUrl: string): Promise<ThumbnailLookup> {
  try {
    const res = await fetch(cacheUrl, {
      method: "GET",
      headers: sasVersionHeaders(cacheUrl, logSavedBrandTemplatesError, "brand-templates-thumbnail-sas-version-parse"),
    });
    if (!res.ok) throw new Error(`Thumbnail cache download failed (${res.status})`);
    return buildBrandThumbnailLookup(JSON.parse(await res.text()));
  } catch (error) {
    logSavedBrandTemplatesError(
      "brand-templates-thumbnail-cache-fetch",
      getErrorMessage(error, "Thumbnail cache fetch/parse failed"),
    );
    return EMPTY_LOOKUP;
  }
}

function loadLookup(cacheUrl: string): Promise<ThumbnailLookup> {
  let cached = lookupCache.get(cacheUrl);
  if (!cached) {
    cached = downloadLookup(cacheUrl);
    // Don't pin a failed download for the rest of the session.
    void cached.then((lookup) => {
      if (lookup === EMPTY_LOOKUP) lookupCache.delete(cacheUrl);
    });
    lookupCache.set(cacheUrl, cached);
  }
  return cached;
}

/** Test seam — drops the module-level download cache. */
export function resetBrandThumbnailCache(): void {
  lookupCache.clear();
}

/** Thumbnail key -> `data:` URL for the given cache files; empty until downloads settle or on failure. */
export function useBrandThumbnails(cacheUrls: readonly (string | null | undefined)[]): ThumbnailLookup {
  const [lookup, setLookup] = useState<ThumbnailLookup>(EMPTY_LOOKUP);
  // A stable primitive to depend on — the array itself is new every render.
  const urlsKey = [...new Set(cacheUrls.filter((url): url is string => typeof url === "string" && url !== ""))].join("\n");

  useEffect(() => {
    if (!urlsKey) {
      setLookup(EMPTY_LOOKUP);
      return;
    }
    let cancelled = false;
    void Promise.all(urlsKey.split("\n").map(loadLookup)).then((lookups) => {
      if (cancelled) return;
      const merged = new Map<string, string>();
      for (const part of lookups) for (const [key, value] of part) if (!merged.has(key)) merged.set(key, value);
      setLookup(merged);
    });
    return () => {
      cancelled = true;
    };
  }, [urlsKey]);

  return lookup;
}
